import { INITIAL_LAYOUT_STATE } from 'BomdisoftComponents/layout/reducer';
import type { LayoutMachineContext } from 'BomdisoftComponents/layout/types';
import { custom, persisted } from '@bomdisoft/store/persisted';

const STORAGE_KEY = '@bomdisoft-layout-state';

/**
 * What a browser has stored, which is deliberately a PARTIAL layout.
 *
 * Version 1 payloads persisted `sidebarOpen` on every mount, including the
 * mount that merely wrote the old default, so a stored `false` cannot be told
 * apart from a visitor's own choice. Such a payload keeps its drawers and
 * contributes no sidebar at all, which is why the stored type has to be able to
 * omit one: filling in a default here would be indistinguishable from a choice,
 * and would silently outrank the `defaultSidebarOpen` the app declared.
 *
 * Versions 1 and 2 both wrote a `{ context, v }` wrapper, which `migrate`
 * reads; version 3 is the handle's own envelope.
 */
type StoredLayout = Partial<LayoutMachineContext>;

export const layoutState = persisted<StoredLayout>({
  codec: custom<StoredLayout>(
    raw => {
      try {
        const parsed: unknown = JSON.parse(raw);
        if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return undefined;
        return parsed as StoredLayout;
      } catch {
        return undefined;
      }
    },
    value => JSON.stringify(value),
  ),
  default: {},
  in: ['local'],
  key: STORAGE_KEY,
  migrate: (raw, from) => {
    try {
      const parsed = JSON.parse(raw) as { context?: StoredLayout; snapshot?: { context?: StoredLayout } };
      const context = parsed?.snapshot?.context ?? parsed?.context;
      if (!context) return undefined;
      if (from >= 2) return context;
      const { sidebarOpen: _untrusted, ...kept } = context;
      return kept;
    } catch {
      return undefined;
    }
  },
  version: 3,
});

export const toInitialLayoutState = (defaultSidebarOpen: boolean): LayoutMachineContext => {
  const initial = { ...INITIAL_LAYOUT_STATE, sidebarOpen: defaultSidebarOpen };
  if (typeof window === 'undefined') return initial;
  const stored = layoutState.read();
  return { ...initial, ...stored, drawers: stored.drawers ?? {} };
};
